import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { getDashboardData } from "./services/dashboardService";
import {
  coerceLegacyDateRangeToFilters,
  createDefaultDashboardFilters,
  getLegacySelectedTimeRange,
  mergeDashboardFilters,
  resetDashboardFilters,
  updateDashboardFilterValue,
} from "./dashboardFilters";

export const DashboardContext = createContext();

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error(
      "useDashboardContext must be used within a DashboardProvider"
    );
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const [dashboardFilters, setDashboardFilters] = useState(() =>
    createDefaultDashboardFilters()
  );
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds

  // Dashboard data state
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const selectedTimeRange = useMemo(
    () => getLegacySelectedTimeRange(dashboardFilters),
    [dashboardFilters]
  );

  // Function to fetch dashboard data
  const fetchDashboardData = useCallback(async (
    isRefresh = false,
    filtersOverride = null
  ) => {
    let isMounted = true;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const activeFilters = filtersOverride || dashboardFilters;
      const res = await getDashboardData(activeFilters);
      if (isMounted) {
        if (res.success) {
          setData(res.data);
          setError(null);
          setLastRefreshed(new Date());
        } else {
          setError(res.error || "Failed to fetch dashboard data");
          // Keep last good payload on refresh failure so the board does not
          // collapse into empty "Awaiting experiment metrics" defaults.
          if (!isRefresh) {
            setData({});
          }
        }
      }
    } catch (err) {
      if (isMounted) {
        setError(err.message || "Failed to fetch dashboard data");
        if (!isRefresh) {
          setData({});
        }
      }
    } finally {
      if (isMounted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [dashboardFilters]);

  // Initial data fetch and global filter-driven refreshes
  useEffect(() => {
    fetchDashboardData();
    return () => {
      // Cleanup
    };
  }, [fetchDashboardData]);

  // Function to refresh dashboard data
  const refreshDashboard = useCallback(() => {
    fetchDashboardData(true);
  }, [fetchDashboardData]);

  const updateDashboardFilters = useCallback((updates) => {
    setDashboardFilters((current) => mergeDashboardFilters(current, updates));
  }, []);

  const replaceDashboardFilters = useCallback((nextFilters) => {
    setDashboardFilters((current) => {
      const resolvedFilters =
        typeof nextFilters === "function" ? nextFilters(current) : nextFilters;
      return mergeDashboardFilters(current, resolvedFilters);
    });
  }, []);

  const updateDashboardFilter = useCallback((group, key, value) => {
    setDashboardFilters((current) =>
      updateDashboardFilterValue(current, group, key, value)
    );
  }, []);

  const resetFilters = useCallback((overrides = {}) => {
    setDashboardFilters(resetDashboardFilters(overrides));
  }, []);

  const setTimePreset = useCallback((datePreset) => {
    updateDashboardFilters({
      time: {
        datePreset,
        startDate: null,
        endDate: null,
      },
    });
  }, [updateDashboardFilters]);

  const setCustomDateRange = useCallback((startDate, endDate) => {
    updateDashboardFilters({
      time: {
        datePreset: "custom",
        startDate,
        endDate,
      },
    });
  }, [updateDashboardFilters]);

  const setSelectedTimeRange = useCallback((dateRange) => {
    setDashboardFilters((current) =>
      coerceLegacyDateRangeToFilters(dateRange, current)
    );
  }, []);

  // NEW: Function to refresh with custom date range
  const refreshWithDateRange = useCallback((dateRange) => {
    setDashboardFilters((current) =>
      coerceLegacyDateRangeToFilters(dateRange, current)
    );
  }, []);

  // NEW: Function to get analytics data for specific date range
  const getAnalyticsForDateRange = useCallback(async (dateRange) => {
    try {
      setRefreshing(true);
      const analyticsFilters = coerceLegacyDateRangeToFilters(
        dateRange,
        dashboardFilters
      );
      const res = await getDashboardData(analyticsFilters);
      if (res.success) {
        // Update only analytics data, preserve other data
        setData((prev) => ({
          ...prev,
          funnelAnalytics: res.data.funnelAnalytics,
          campaignPerformance: res.data.campaignPerformance,
        }));
        return res.data;
      } else {
        throw new Error(res.error || "Failed to fetch analytics data");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch analytics data");
      throw err;
    } finally {
      setRefreshing(false);
    }
  }, [dashboardFilters]);

  const value = {
    dashboardFilters,
    setDashboardFilters: replaceDashboardFilters,
    updateDashboardFilters,
    updateDashboardFilter,
    resetDashboardFilters: resetFilters,
    setTimePreset,
    setCustomDateRange,
    selectedTimeRange,
    setSelectedTimeRange,
    selectedPlatform,
    setSelectedPlatform,
    refreshInterval,
    setRefreshInterval,
    data,
    loading,
    error,
    refreshing,
    refreshDashboard,
    lastRefreshed,
    // NEW: Enhanced functionality
    refreshWithDateRange,
    getAnalyticsForDateRange,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};
